import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Skeleton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  AccountBalanceWallet,
  TrendingUp,
  Send,
  Refresh,
  AccountBalance,
  CheckCircle,
  Schedule,
  Cancel,
  ArrowUpward,
  ArrowDownward,
  Store as StoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS, PALETTE_LIGHT } from '../../theme/tokens';
import DesktopNav from '../../components/DesktopNav';
import LibrarianBottomNav from '../../components/LibrarianBottomNav';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const MotionCard = motion(Card);
const MotionBox = motion(Box);

function LibrarianWallet() {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Commission owed to BML
  const [commissionOwed, setCommissionOwed] = useState(null);
  const [commissionBreakdown, setCommissionBreakdown] = useState([]);
  const [commissionLoading, setCommissionLoading] = useState(true);
  const [commissionSettling, setCommissionSettling] = useState(false);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('upi');
  const [upiId, setUpiId] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchWalletData();
    fetchCommissionOwed();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletRes, transactionsRes, withdrawalsRes] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/transactions?limit=20'),
        api.get('/wallet/withdrawals?limit=10')
      ]);

      setWallet(walletRes.data.data.wallet || walletRes.data.data);
      setTransactions(transactionsRes.data.data.transactions || []);
      setWithdrawals(withdrawalsRes.data.data.withdrawals || []);
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setSnackbar({ open: true, message: 'Failed to load wallet data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCommissionOwed = async () => {
    try {
      setCommissionLoading(true);
      const res = await api.get('/librarian/commission/owed');
      const data = res.data.data || res.data;
      setCommissionOwed(data.total_owed_display || '₹0');
      setCommissionBreakdown(data.breakdown || []);
    } catch (err) {
      console.error('Error fetching commission owed:', err);
      setCommissionOwed('₹0');
      setCommissionBreakdown([]);
    } finally {
      setCommissionLoading(false);
    }
  };

  const handleSettleCommission = async () => {
    try {
      setCommissionSettling(true);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setSnackbar({ open: true, message: 'Failed to load payment gateway. Please try again.', severity: 'error' });
        setCommissionSettling(false);
        return;
      }

      const settleRes = await api.post('/librarian/commission/settle');
      const { settlement, order } = settleRes.data.data || settleRes.data;
      const { order_id, amount: orderAmount, currency, key_id, prefill } = order;

      const options = {
        key: key_id,
        amount: orderAmount,
        currency: currency || 'INR',
        name: 'BookMyLibrary',
        description: `BML Commission Settlement`,
        order_id: order_id,
        prefill: {
          name: prefill?.name || '',
          email: prefill?.email || '',
          contact: prefill?.contact || '',
        },
        theme: { color: PALETTE_LIGHT.primary.main },
        handler: async function (response) {
          try {
            await api.post('/librarian/commission/settle/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSnackbar({ open: true, message: `Commission of ${settlement?.amount_display || ''} settled successfully!`, severity: 'success' });
            await fetchCommissionOwed();
          } catch (verifyErr) {
            console.error('Commission settlement verification failed:', verifyErr);
            setSnackbar({ open: true, message: 'Payment received but verification failed. Please contact support.', severity: 'error' });
          }
          setCommissionSettling(false);
        },
        modal: {
          ondismiss: function () {
            setCommissionSettling(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setCommissionSettling(false);
        setSnackbar({ open: true, message: response.error.description || 'Payment failed. Please try again.', severity: 'error' });
      });
      razorpay.open();
    } catch (err) {
      console.error('Error initiating commission settlement:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to initiate payment', severity: 'error' });
      setCommissionSettling(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 100) {
      setError('Minimum withdrawal amount is Rs.100');
      return;
    }

    if (withdrawMethod === 'upi' && !upiId) {
      setError('Please enter your UPI ID');
      return;
    }

    if (withdrawMethod === 'bank' && (!bankName || !bankAccount || !bankIfsc)) {
      setError('Please fill all bank details');
      return;
    }

    try {
      setActionLoading(true);
      setError('');

      await api.post('/wallet/withdraw', {
        amount,
        method: withdrawMethod,
        upi_id: withdrawMethod === 'upi' ? upiId : null,
        bank_account_name: withdrawMethod === 'bank' ? bankName : null,
        bank_account_number: withdrawMethod === 'bank' ? bankAccount : null,
        bank_ifsc: withdrawMethod === 'bank' ? bankIfsc : null,
      });

      setSnackbar({ open: true, message: 'Withdrawal request submitted!', severity: 'success' });
      setWithdrawDialog(false);
      resetWithdrawForm();
      fetchWalletData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit withdrawal request');
    } finally {
      setActionLoading(false);
    }
  };

  const resetWithdrawForm = () => {
    setWithdrawAmount('');
    setUpiId('');
    setBankName('');
    setBankAccount('');
    setBankIfsc('');
    setError('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
        <DesktopNav />
        <Box sx={{ minHeight: 'calc(100vh - 64px)', pb: '90px' }}>
          <Container maxWidth="sm" sx={{ py: 2 }}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: RADIUS.xl, mb: 2 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: RADIUS.xl }} />
          </Container>
        </Box>
        <LibrarianBottomNav />
      </Box>
    );
  }

  const balance = wallet?.balance || 0;
  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending' || w.status === 'processing');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <DesktopNav />
      <Container maxWidth="sm" sx={{ py: 2, pb: '110px' }}>
        <MotionBox initial="hidden" animate="visible" variants={containerVariants}>
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
              >
                <ArrowBack />
              </IconButton>
              <Box flex={1}>
                <Typography variant="h5" fontWeight="800">My Earnings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your wallet & withdrawals
                </Typography>
              </Box>
              <IconButton onClick={fetchWalletData}>
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Commission Owed to BML */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 3,
              border: 1,
              borderColor: commissionBreakdown.length > 0 ? 'warning.main' : 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: RADIUS.md,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'warning.light',
                    }}
                  >
                    <StoreIcon color="warning" />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Owed to BML
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Commission from pay-at-library bookings
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={fetchCommissionOwed}
                  aria-label="Refresh commission owed"
                  disabled={commissionLoading}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Box>

              {commissionLoading ? (
                <Box>
                  <Skeleton variant="text" height={48} width="50%" />
                  <Skeleton variant="text" height={24} width="80%" />
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    color={commissionBreakdown.length > 0 ? 'warning.main' : 'text.secondary'}
                    mb={1}
                  >
                    {commissionOwed}
                  </Typography>

                  {commissionBreakdown.length > 0 ? (
                    <>
                      <List disablePadding sx={{ mb: 2 }}>
                        {commissionBreakdown.slice(0, 5).map((item, index) => (
                          <ListItem
                            key={item.ledger_id || index}
                            divider={index < Math.min(commissionBreakdown.length, 5) - 1}
                            sx={{ px: 0, py: 1 }}
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: '65%' }}>
                                    {item.student_name || `Booking #${item.booking_id}`}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={700} color="warning.main">
                                    {item.amount_display}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary">
                                  {item.created_at
                                    ? new Date(item.created_at).toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                      })
                                    : ''}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                        {commissionBreakdown.length > 5 && (
                          <ListItem sx={{ px: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                              +{commissionBreakdown.length - 5} more entries
                            </Typography>
                          </ListItem>
                        )}
                      </List>

                      <Button
                        fullWidth
                        variant="contained"
                        color="warning"
                        size="large"
                        disabled={commissionSettling}
                        onClick={handleSettleCommission}
                        sx={{ borderRadius: RADIUS.lg, py: 1.5, fontWeight: 700 }}
                      >
                        {commissionSettling ? 'Processing...' : `Settle ${commissionOwed} Now`}
                      </Button>
                    </>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <CheckCircle color="success" fontSize="small" />
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        All settled — no outstanding commission
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" alignItems="flex-start" gap={1} mt={2}>
                    <InfoIcon fontSize="small" color="action" sx={{ mt: 0.2, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">
                      This is the BML commission owed for "Pay at Library" bookings. Settle it via Razorpay to keep your account in good standing.
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </MotionCard>

          {/* Balance Card */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 3,
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              color: 'white'
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <AccountBalanceWallet sx={{ fontSize: 48, mb: 1, opacity: 0.9 }} />
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Available Balance
              </Typography>
              <Typography variant="h2" fontWeight="800">
                Rs.{(balance / 100).toFixed(2)}
              </Typography>
              {pendingWithdrawals.length > 0 && (
                <Chip
                  icon={<Schedule />}
                  label={`${pendingWithdrawals.length} pending withdrawal(s)`}
                  size="small"
                  sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              )}
            </CardContent>
          </MotionCard>

          {/* Withdraw Button */}
          <MotionBox variants={itemVariants} mb={3}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Send />}
              onClick={() => setWithdrawDialog(true)}
              disabled={balance < 10000}
              sx={{
                borderRadius: RADIUS.lg,
                py: 1.5,
                fontWeight: 700,
                background: GRADIENTS.brand
              }}
            >
              Request Withdrawal
            </Button>
            {balance < 10000 && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
                Minimum Rs.100 required to withdraw
              </Typography>
            )}
          </MotionBox>

          {/* Recent Withdrawals */}
          {withdrawals.length > 0 && (
            <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="700" gutterBottom display="flex" alignItems="center" gap={1}>
                  <AccountBalance />
                  Withdrawal Requests
                </Typography>
                <List disablePadding>
                  {withdrawals.slice(0, 5).map((withdrawal, index) => (
                    <ListItem
                      key={withdrawal.id}
                      divider={index < Math.min(withdrawals.length, 5) - 1}
                      sx={{ px: 0 }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="600">
                              Rs.{(withdrawal.amount / 100).toFixed(2)}
                            </Typography>
                            <Chip
                              label={withdrawal.status.toUpperCase()}
                              color={getStatusColor(withdrawal.status)}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {withdrawal.method === 'upi' ? `UPI: ${withdrawal.upi_id}` : `Bank: ****${withdrawal.bank_account_number || ''}`}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {new Date(withdrawal.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </MotionCard>
          )}

          {/* Transaction History */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="700" gutterBottom display="flex" alignItems="center" gap={1}>
                <TrendingUp />
                Transaction History
              </Typography>

              {transactions.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <AccountBalanceWallet sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography color="text.secondary">No transactions yet</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Earnings from bookings will appear here
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {transactions.map((tx, index) => (
                    <ListItem
                      key={tx.id}
                      divider={index < transactions.length - 1}
                      sx={{ px: 0, py: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: RADIUS.md,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: tx.type === 'credit' ? 'success.light' : 'error.light',
                          mr: 2
                        }}
                      >
                        {tx.type === 'credit' ? (
                          <ArrowDownward color="success" />
                        ) : (
                          <ArrowUpward color="error" />
                        )}
                      </Box>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" fontWeight="600" noWrap sx={{ maxWidth: '60%' }}>
                              {tx.description || (tx.type === 'credit' ? 'Credit' : 'Debit')}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              color={tx.type === 'credit' ? 'success.main' : 'error.main'}
                            >
                              {tx.amount_display || `${tx.type === 'credit' ? '+' : '-'}Rs.${(tx.amount / 100).toFixed(2)}`}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(tx.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>

      {/* Withdraw Dialog */}
      <Dialog
        open={withdrawDialog}
        onClose={() => {
          setWithdrawDialog(false);
          resetWithdrawForm();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.xl } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="700">Request Withdrawal</Typography>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Amount (Rs.)"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            inputProps={{ min: 100, max: balance / 100 }}
            sx={{ mb: 2 }}
            helperText={`Available: Rs.${(balance / 100).toFixed(2)} | Minimum: Rs.100`}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Withdrawal Method</InputLabel>
            <Select
              value={withdrawMethod}
              label="Withdrawal Method"
              onChange={(e) => setWithdrawMethod(e.target.value)}
            >
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="bank">Bank Transfer</MenuItem>
            </Select>
          </FormControl>

          {withdrawMethod === 'upi' ? (
            <TextField
              fullWidth
              label="UPI ID"
              placeholder="yourname@upi"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
              sx={{ mb: 2 }}
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Account Number"
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="IFSC Code"
                value={bankIfsc}
                onChange={(e) => setBankIfsc(e.target.value.toUpperCase())}
                sx={{ mb: 2 }}
              />
            </>
          )}

          <Alert severity="info" sx={{ borderRadius: RADIUS.md }}>
            Withdrawals are processed within 2-3 business days.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setWithdrawDialog(false);
              resetWithdrawForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWithdraw}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            sx={{
              background: GRADIENTS.brand
            }}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {snackbar.open && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000
          }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ borderRadius: RADIUS.lg, boxShadow: 6 }}
          >
            {snackbar.message}
          </Alert>
        </Box>
      )}

      <LibrarianBottomNav />
    </Box>
  );
}

export default LibrarianWallet;
